import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Timer, ChartLineUp, MapPinSimpleArea } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import Calculator from '../components/Calculator';
import FAQ, { FaqJsonLd } from '../components/FAQ';
import { TrustBadges } from '../components/TrustBadges';
import LiveCounter from '../components/LiveCounter';
import { FEATURED_SEO_LIST as SEO_LIST } from '../lib/seoPages';
import { CITIES } from '../lib/cityData';

export default function HomePage() {
  return (
    <main data-testid="home-page">
      <Helmet>
        <title>PendelPilot · Pendelkosten & Lebenszeit in 30 Sekunden berechnen</title>
        <meta
          name="description"
          content="PendelPilot: berechne Pendelkosten, Lebenszeit-Verlust & Jobwechsel-Vorteile in 30 Sekunden. Mit Spritpreis-Automatik, Pendlerpauschale und Smart-Empfehlungen."
        />
        <meta property="og:title" content="PendelPilot — Bessere Entscheidungen zum Arbeitsweg" />
        <meta property="og:description" content="In 30 Sekunden zu ehrlichen Pendelkosten und konkreten Empfehlungen." />
      </Helmet>
      <FaqJsonLd />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero">
        <img
          src="https://images.unsplash.com/photo-1653453502494-f4bdb72bc062?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHx0cmFmZmljJTIwamFtJTIwc3Vuc2V0JTIwb3ZlcmhlYWR8ZW58MHx8fHwxNzgwMzA2ODI4fDA&ixlib=rb-4.1.0&q=85"
          alt="Stau aus der Luft"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-24 md:pt-32 md:pb-40">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 border border-neutral-900 px-3 h-8 font-mono text-xs uppercase tracking-widest bg-white">
              <span className="w-1.5 h-1.5 rounded-full bg-[#2A9D8F]" /> Berechnung in 30 Sekunden
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tighter mt-6 max-w-4xl leading-[0.95]">
              Wie viel kostet dich dein Arbeitsweg <span className="italic font-medium">wirklich?</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-neutral-700 max-w-2xl leading-relaxed">
              Sprit, Verschleiß, Lebenszeit — und konkrete Empfehlungen, wie du sparst. Kostenlos, ohne Anmeldung, direkt in deinem Browser.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#calculator"
                data-testid="hero-cta"
                className="inline-flex items-center gap-2 bg-neutral-900 text-white h-14 px-6 text-sm uppercase tracking-widest hover:bg-black"
              >
                Jetzt Pendel-Score berechnen
                <ArrowRight size={16} weight="bold" />
              </a>
              <Link
                to="/jobwechsel-rechner"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-neutral-900 h-14 px-6 text-sm uppercase tracking-widest hover:bg-neutral-900 hover:text-white"
              >
                Jobwechsel-Rechner
              </Link>
            </div>
            <TrustBadges className="mt-8" />
            <LiveCounter />
          </motion.div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-16">
        <div className="grid grid-cols-1 md:grid-cols-3 grid-border">
          <ValueProp
            num="01"
            icon={<ChartLineUp size={22} weight="bold" />}
            title="Persönlicher Pendel-Score"
            text="Score von 0 bis 100 — bewertet Strecke, Zeit, Kosten und Office-Tage. Mit klarer Empfehlung."
          />
          <ValueProp
            num="02"
            icon={<Timer size={22} weight="bold" />}
            title="Lebenszeit-Rechner"
            text="Sieh in Tagen pro Jahr, wie viel echte Lebenszeit dein Pendeln kostet — nicht nur in Euro."
          />
          <ValueProp
            num="03"
            icon={<MapPinSimpleArea size={22} weight="bold" />}
            title="Smart-Empfehlungen"
            text="Homeoffice, Fahrgemeinschaft, Bahn, E-Auto — individuell aus deinen Daten generiert."
          />
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="calculator" className="max-w-7xl mx-auto px-6 md:px-12 mt-24 scroll-mt-20" data-testid="calculator-section">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Rechner</div>
        <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-2 max-w-3xl leading-[0.95]">
          Deine Daten. Dein Pendel-Score. Sofort.
        </h2>
        <p className="text-neutral-700 mt-4 max-w-xl">
          Wähle einen Schnellstart oder passe die Werte an. Du brauchst kein Konto — alle Eingaben bleiben in deinem Browser.
        </p>
        <div className="mt-10">
          <Calculator initialPreset="mittel" />
        </div>
      </section>

      {/* SEO INTERNAL LINKS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Themen-Rechner</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tighter mt-2">Mehr passende Rechner</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 mt-6 grid-border">
          {SEO_LIST.map((p, i) => (
            <Link
              key={p.slug}
              to={`/${p.slug}`}
              data-testid={`seo-link-${p.slug}`}
              className="p-6 border-neutral-200 [&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-b-0 md:[&:not(:last-child)]:border-r hover:bg-neutral-50 group"
            >
              <div className="font-mono text-xs text-neutral-500">{String(i + 1).padStart(2, '0')}</div>
              <div className="font-display text-xl mt-2 group-hover:underline">{p.h1}</div>
              <div className="text-sm text-neutral-600 mt-2 line-clamp-2">{p.intro}</div>
              <div className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-widest">
                Öffnen <ArrowRight size={12} weight="bold" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CITY LINKS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24" data-testid="city-section">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Pendelkosten nach Stadt</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tighter mt-2">Pendelkosten in deiner Stadt</h2>
        <p className="text-neutral-700 mt-3 max-w-2xl">
          Echte Live-Spritpreise und regionale Pendel-Durchschnitte für 20 deutsche Städte.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 mt-6 grid-border">
          {CITIES.map((c) => (
            <Link
              key={c.slug}
              to={`/pendelkosten-${c.slug}`}
              data-testid={`city-link-${c.slug}`}
              className="p-5 border-neutral-200 border-r border-b hover:bg-neutral-900 hover:text-white transition-colors group"
            >
              <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">{c.region}</div>
              <div className="font-display text-xl mt-1">{c.name}</div>
              <div className="text-xs mt-2 opacity-70">Ø {c.avg_distance.toFixed(0)} km · {c.avg_minutes} min</div>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <FAQ />
      </section>
    </main>
  );
}

function ValueProp({ num, icon, title, text }) {
  return (
    <div className="p-8 border-neutral-200 [&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-b-0 md:[&:not(:last-child)]:border-r">
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 bg-neutral-900 text-white flex items-center justify-center">{icon}</div>
        <span className="font-mono text-xs text-neutral-500">{num}</span>
      </div>
      <div className="font-display text-2xl mt-6">{title}</div>
      <p className="text-sm text-neutral-700 mt-2 leading-relaxed">{text}</p>
    </div>
  );
}
